import { apiClient } from './api-client';

// Types based on the API schema
export interface TagRead {
  id?: number | null;
  name: string;
  created_at?: string | null;
}

export interface ExecutionRead {
  id?: number | null;
  analysis_id: number;
  state?: string | null;
  result?: Record<string, any> | null;
  error?: Record<string, any> | null;
  created_at?: string | null;
}

export interface AnalysisRead {
  id?: number | null;
  entry_id: number;
  state?: string | null;
  transcription?: string | null;
  title?: string | null;
  integration?: string | null;
  llm_notes?: string | null;
  fields?: Record<string, any> | null;
  llm_response?: any | null;
  error?: Record<string, any> | null;
  created_at?: string | null;
  executions: ExecutionRead[];
  tags: TagRead[];
}

export interface EntryRead {
  id?: number | null;
  user_id: number;
  type: string;
  file_path: string;
  latitude?: number | null;
  longitude?: number | null;
  created_at?: string | null;
  deleted: boolean;
  external_ids?: Record<string, any> | null;
  analyses: AnalysisRead[];
}

export interface UploadResponse {
  entry_id: number;
}

export interface RerunAnalysisResponse {
  message: string;
  entry_id: number;
}

export interface IntegrationInfo {
  name: string;
  enabled: boolean;
}

export interface IntegrationsResponse {
  integrations: IntegrationInfo[];
}

export interface LocationData {
  latitude: number;
  longitude: number;
}

/**
 * Get current geolocation
 */
const getCurrentLocation = (): Promise<LocationData | null> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      console.warn('Geolocation is not supported by this browser.');
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        console.warn('Error getting user location:', error);
        resolve(null);
      }
    );
  });
};

// Entry and analysis service functions
export const entryService = {
  /**
   * Upload an audio file for analysis
   */
  async uploadAudio(audioFile: File): Promise<UploadResponse> {
    const formData = new FormData();
    
    // Create filename with timestamp
    const timestamp = Date.now();
    const fileExtension = audioFile.type.split('/')[1] || 'webm';
    const filename = `recording_${timestamp}.${fileExtension}`;
    
    formData.append('file', audioFile, filename);
    
    // Add location data if available
    try {
      const location = await getCurrentLocation();
      if (location) {
        formData.append('location', JSON.stringify(location));
      }
    } catch (error) {
      console.warn('Could not get location:', error);
      // Continue without location data
    }
    
    return apiClient.postFormData<UploadResponse>('/upload', formData);
  },
  
  /**
   * Get a specific entry by ID
   */
  async getEntry(entryId: number, latestAnalysis: boolean = true): Promise<EntryRead> {
    return apiClient.get<EntryRead>(`/entry/${entryId}`, { latest_analysis: latestAnalysis });
  },
  
  /**
   * Delete or restore an entry
   */
  async deleteEntry(entryId: number, deleted: boolean = true): Promise<void> {
    return apiClient.delete<void>(`/entry/${entryId}`, { deleted });
  },
  
  /**
   * Get the audio file for an entry
   */
  async getEntryAudioFile(entryId: number): Promise<Blob> {
    const response = await apiClient.get<Blob>(`/entry/${entryId}/file`);
    return response;
  },
  
  /**
   * Re-run analysis on an entry
   */
  async reAnalyzeEntry(entryId: number): Promise<RerunAnalysisResponse> {
    return apiClient.post<RerunAnalysisResponse>(`/entry/${entryId}/analysis`);
  },
  
  /**
   * Get a list of entries with optional filtering
   */
  async getEntries(options?: {
    tags?: string;
    integration?: string;
    limit?: number;
    after_id?: number;
  }): Promise<EntryRead[]> {
    return apiClient.get<EntryRead[]>('/entries', options);
  },
  
  /**
   * Add a tag to an analysis
   */
  async addTagToAnalysis(analysisId: number, tagName: string): Promise<void> {
    return apiClient.post<void>('/analysis/add_tag', { 
      analysis_id: analysisId, 
      tag_name: tagName 
    });
  },
  
  /**
   * Remove a tag from an analysis
   */
  async removeTagFromAnalysis(analysisId: number, tagName: string): Promise<void> {
    return apiClient.post<void>('/analysis/remove_tag', { 
      analysis_id: analysisId, 
      tag_name: tagName 
    });
  },
  
  /**
   * Get available tags
   */
  async getTags(tagPrefix: string = '', limit: number = 10): Promise<TagRead[]> {
    return apiClient.get<TagRead[]>('/tags', { tag_prefix: tagPrefix, limit });
  },
  
  /**
   * Get available integrations
   */
  async getIntegrations(): Promise<IntegrationInfo[]> {
    const response = await apiClient.get<IntegrationsResponse>('/integrations');
    return response.integrations;
  }
};
